export const METHODOLOGY_OTHER_COL = "Methodology used (Other specified)";
export const PROJECT_TYPE_OTHER_COL = "Project Type (Other specified)";
export const OTHER_SPECIFIED = "Other specified";
export const IN_HOUSE_MONTH_COL =
  "Month of energy export (e.g. 01/25, Jan-25)";

const IN_HOUSE_MONTH_COL_ALIASES = [
  IN_HOUSE_MONTH_COL,
  "Month of energy export (e.g. 01/25 or Jan-25)",
  "Month (in 01/25 or Jan-25)",
];

/** Read month value from any known bulk-upload column name. */
export function getInHouseMonthValue(row = {}) {
  for (const key of IN_HOUSE_MONTH_COL_ALIASES) {
    const value = formatBulkUploadCellValue(row?.[key]);
    if (value) return { key, value };
  }
  return { key: IN_HOUSE_MONTH_COL, value: "" };
}

export const isOtherSpecified = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "other specified" || normalized === "others";
};

/** Read entered excel/form value with column name fallbacks. */
export function getInHouseFieldEnteredValue(
  item,
  row,
  enteredKey,
  fieldKey = enteredKey,
) {
  const keys =
    enteredKey === "Methodology used" || fieldKey === "Methodology used"
      ? ["Methodology used", "Methodology Used", "methodologyUsed"]
      : enteredKey === "Project Type" || fieldKey === "Project Type"
        ? ["Project Type", "projectType"]
        : [enteredKey, fieldKey];

  for (const key of keys) {
    if (!key) continue;
    const val = String(item?.[key] ?? row?.[key] ?? "").trim();
    if (val) return val;
  }
  return "";
};

export const MIN_IN_HOUSE_OTHER_NAME_LENGTH = 2;

const INTERNAL_BULK_ROW_KEYS = new Set(["__excelRowNumber"]);

/** Case-insensitive exact match against a master dropdown list. */
export function isExactMasterOptionMatch(value, options = []) {
  const normalized = String(value ?? "").trim();
  if (!normalized) return false;
  return options.some(
    (opt) => String(opt).trim().toLowerCase() === normalized.toLowerCase(),
  );
}

/** Other-specified name is filled when the custom input has a value. */
export function isInHouseOtherFieldResolved(row, otherColKey) {
  return Boolean(String(row?.[otherColKey] ?? "").trim());
}

/** Field is valid for confirm upload. */
export function isInHouseMasterFieldResolved(
  item,
  row,
  fieldKey,
  otherColKey,
  options,
  enteredKey,
) {
  const entered = getInHouseFieldEnteredValue(item, row, enteredKey, fieldKey);
  const selected = String(row?.[fieldKey] ?? "").trim();

  if (
    selected &&
    !isOtherSpecified(selected) &&
    isExactMasterOptionMatch(selected, options)
  ) {
    return true;
  }

  if (isOtherSpecified(entered) || isOtherSpecified(selected)) {
    const custom = String(row?.[otherColKey] ?? "").trim();
    if (!custom) return false;
    const applied = String(row?.[fieldKey] ?? "").trim();
    return (
      Boolean(applied) &&
      !isOtherSpecified(applied) &&
      isExactMasterOptionMatch(applied, options)
    );
  }

  if (selected && !isOtherSpecified(selected)) {
    return isExactMasterOptionMatch(selected, options);
  }

  return isExactMasterOptionMatch(entered, options);
}

/** True when this column shows an input/dropdown that still needs user action. */
export function isInHouseMasterFieldNeedsInput(
  item,
  row,
  fieldKey,
  otherColKey,
  options,
  enteredKey,
) {
  if (isInHouseMasterFieldResolved(item, row, fieldKey, otherColKey, options, enteredKey)) {
    return false;
  }

  const entered = getInHouseFieldEnteredValue(item, row, enteredKey, fieldKey);
  const selected = String(row?.[fieldKey] ?? "").trim();

  if (isOtherSpecified(entered) || isOtherSpecified(selected)) {
    return true;
  }

  return !isExactMasterOptionMatch(entered, options);
}

/** Show Matched chip only when value exactly matches a master option. */
export function isInHouseMasterFieldMatched(
  item,
  row,
  fieldKey,
  otherColKey,
  options,
  enteredKey,
) {
  const entered = getInHouseFieldEnteredValue(item, row, enteredKey, fieldKey);
  const selected = String(row?.[fieldKey] ?? "").trim();

  if (
    selected &&
    !isOtherSpecified(selected) &&
    isExactMasterOptionMatch(selected, options)
  ) {
    return true;
  }

  if (isOtherSpecified(entered) || isOtherSpecified(selected)) {
    const custom = String(row?.[otherColKey] ?? "").trim();
    const applied = String(row?.[fieldKey] ?? "").trim();
    const valueToMatch = !isOtherSpecified(applied) && applied ? applied : custom;
    if (!valueToMatch) return false;
    return isExactMasterOptionMatch(valueToMatch, options);
  }

  if (selected) return isExactMasterOptionMatch(selected, options);
  return isExactMasterOptionMatch(entered, options);
}

/** True when the row has a month value (only these rows belong in the review popup). */
export function hasInHouseMonthValue(row = {}) {
  return Boolean(getInHouseMonthValue(row).value);
}

/** Normalize a bulk-upload row (month aliases, excel row number). */
export function normalizeInHouseBulkUploadRow(row = {}, excelRowNumber = null) {
  const next = { ...row };
  const { key, value } = getInHouseMonthValue(next);
  if (value) {
    IN_HOUSE_MONTH_COL_ALIASES.forEach((alias) => {
      if (alias !== IN_HOUSE_MONTH_COL) delete next[alias];
    });
    next[IN_HOUSE_MONTH_COL] = value;
    if (key !== IN_HOUSE_MONTH_COL) delete next[key];
  }
  if (excelRowNumber != null && Number.isFinite(Number(excelRowNumber))) {
    next.__excelRowNumber = Number(excelRowNumber);
  }
  return next;
}

function uniqueInHouseDetailRows(rows = []) {
  const byKey = new Map();
  rows.forEach((row) => {
    const normalized = normalizeInHouseBulkUploadRow(row);
    const key = [
      normalized["S No."],
      normalized["Project name"],
      getInHouseMonthValue(normalized).value,
    ].join("|");
    if (!byKey.has(key)) {
      byKey.set(key, normalized);
    }
  });
  return [...byKey.values()];
}

/** Keep only rows where month of energy export is filled. */
export function filterInHouseRowsWithMonth(rows = []) {
  if (!Array.isArray(rows)) return [];
  return rows.filter(hasInHouseMonthValue);
}

/** Remove parse-only fields before json upload. */
export function stripInHouseBulkUploadInternalFields(row = {}) {
  if (!row || typeof row !== "object") return row;
  return Object.fromEntries(
    Object.entries(row).filter(([key]) => !INTERNAL_BULK_ROW_KEYS.has(key)),
  );
}

/** Map API detail row number to 0-based array index. */
export function resolveInHouseBulkRowIndex(rows, rowNum) {
  const n = Number(rowNum);
  if (!Number.isFinite(n) || n < 1) return -1;

  const byStoredExcelRow = rows.findIndex(
    (r) => Number(r?.__excelRowNumber) === n,
  );
  if (byStoredExcelRow >= 0) return byStoredExcelRow;

  const bySerial = rows.findIndex((r) => Number(r?.["S No."]) === n);
  if (bySerial >= 0) return bySerial;

  const byExcelRow = n - 2;
  if (byExcelRow >= 0 && byExcelRow < rows.length) return byExcelRow;

  const byOneBased = n - 1;
  if (byOneBased >= 0 && byOneBased < rows.length) return byOneBased;

  return -1;
}

const detailReason = (detail) =>
  String(detail?.reason ?? detail?.error ?? detail?.message ?? "").trim();

/** Normalize ExcelJS / CSV cell values to plain strings. */
export function formatBulkUploadCellValue(val) {
  if (val === null || val === undefined) return "";
  if (typeof val === "object") {
    if (val.text !== undefined) return String(val.text).trim();
    if (Array.isArray(val.richText)) {
      return val.richText.map((part) => part?.text || "").join("").trim();
    }
    if (val.result !== undefined && val.result !== null) {
      return formatBulkUploadCellValue(val.result);
    }
    if (val instanceof Date) {
      const month = String(val.getMonth() + 1).padStart(2, "0");
      const year = String(val.getFullYear()).slice(-2);
      return `${month}/${year}`;
    }
  }
  return String(val).trim();
}

function parseCsvLine(line) {
  const values = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      values.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  values.push(current);
  return values.map((value) => value.trim());
}

async function parseUploadedCsvFile(file) {
  const text = await file.text();
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];

  const headers = parseCsvLine(lines[0]).map((header) => header.trim());
  const rows = [];

  for (let i = 1; i < lines.length; i += 1) {
    const values = parseCsvLine(lines[i]);
    const row = {};
    headers.forEach((header, index) => {
      if (!header) return;
      row[header] = formatBulkUploadCellValue(values[index] ?? "");
    });
    if (Object.keys(row).length > 0) {
      row.__excelRowNumber = i + 1;
      rows.push(row);
    }
  }

  return filterInHouseRowsWithMonth(rows);
}

/** Pull row objects embedded inside API detail entries. */
function extractRowsFromDetails(details = []) {
  if (!Array.isArray(details)) return [];
  const rows = [];
  details.forEach((detail) => {
    const raw =
      detail?.entry ||
      detail?.rowData ||
      detail?.data ||
      detail?.record ||
      detail?.excelRow ||
      detail?.rowObject;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return;
    rows.push(
      normalizeInHouseBulkUploadRow(raw, detail?.row ?? raw?.__excelRowNumber),
    );
  });
  return uniqueInHouseDetailRows(rows);
}

/** Parse uploaded .xlsx / .csv bulk file into row objects. */
export async function parseUploadedExcelFile(file) {
  if (!file) return [];

  const fileName = String(file.name || "").toLowerCase();
  if (fileName.endsWith(".csv")) {
    return parseUploadedCsvFile(file);
  }

  const ExcelJS = (await import("exceljs")).default;
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const sheet =
    workbook.getWorksheet("Template") ||
    workbook.worksheets.find((ws) => ws?.name?.toLowerCase() !== "valid dropdown options") ||
    workbook.worksheets[0];
  if (!sheet) return [];

  const headerRow = sheet.getRow(1);
  const headers = [];
  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    headers[colNumber - 1] = formatBulkUploadCellValue(cell.value);
  });

  const rows = [];
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === 1) return;
    const obj = {};
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const key = headers[colNumber - 1];
      if (!key) return;
      obj[key] = formatBulkUploadCellValue(cell.value);
    });
    if (Object.keys(obj).length > 0) {
      obj.__excelRowNumber = rowNumber;
      rows.push(obj);
    }
  });

  return filterInHouseRowsWithMonth(rows);
}

export const isMethodologyOtherDetail = (reason) => {
  const r = reason.toLowerCase();
  return (
    r.includes("methodology") &&
    (r.includes("other specified") ||
      r.includes("others") ||
      r.includes("methodology used (other") ||
      r.includes("methodology name"))
  );
};

export const isProjectTypeOtherDetail = (reason) => {
  const r = reason.toLowerCase();
  return (
    r.includes("project type") &&
    (r.includes("other specified") ||
      r.includes("others") ||
      r.includes("project type (other") ||
      r.includes("project type name"))
  );
};

export const isMethodologyUnmatchedDetail = (reason) => {
  const r = reason.toLowerCase();
  return r.includes("methodology") && !isMethodologyOtherDetail(reason);
};

export const isProjectTypeUnmatchedDetail = (reason) => {
  const r = reason.toLowerCase();
  return r.includes("project type") && !isProjectTypeOtherDetail(reason);
};

export function withInHouseDefaultMatchFlags(row = {}) {
  const methodology = String(
    row?.["Methodology used"] ?? row?.["Methodology Used"] ?? "",
  ).trim();
  const projectType = String(row?.["Project Type"] ?? "").trim();
  const methodologyOther = String(row?.[METHODOLOGY_OTHER_COL] ?? "").trim();
  const projectTypeOther = String(
    row?.[PROJECT_TYPE_OTHER_COL] ?? "",
  ).trim();

  const apiMethodologyMatched =
    row.matchedMethodologyUsed === true || row.matchedMethodology === true;
  const apiProjectTypeMatched = row.matchedProjectType === true;

  const methodologyOk =
    apiMethodologyMatched ||
    (isOtherSpecified(methodology) && Boolean(methodologyOther));

  const projectTypeOk =
    apiProjectTypeMatched ||
    (isOtherSpecified(projectType) && Boolean(projectTypeOther));

  return {
    ...row,
    matchedMethodologyUsed: methodologyOk,
    matchedProjectType: projectTypeOk,
  };
}

/** Apply API `details: [{ row, reason }]` to review rows + field errors. */
export function applyInHouseBulkUploadDetails(rows = [], details = []) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { rows: [], fieldErrors: {}, autoShowInput: {} };
  }

  const nextRows = rows.map((row) => withInHouseDefaultMatchFlags({ ...row }));
  const fieldErrors = {};
  const autoShowInput = {};

  if (!Array.isArray(details) || details.length === 0) {
    return { rows: nextRows, fieldErrors, autoShowInput };
  }

  details.forEach((detail) => {
    let idx = resolveInHouseBulkRowIndex(nextRows, detail?.row);

    if (idx < 0 && detail?.entry) {
      const entryRow = normalizeInHouseBulkUploadRow(
        detail.entry,
        detail?.row ?? detail.entry?.__excelRowNumber,
      );
      idx = resolveInHouseBulkRowIndex(nextRows, entryRow?.["S No."]);
      if (idx < 0) {
        idx = nextRows.findIndex(
          (row) =>
            String(row?.["Project name"] ?? "").trim() ===
              String(entryRow?.["Project name"] ?? "").trim() &&
            getInHouseMonthValue(row).value ===
              getInHouseMonthValue(entryRow).value,
        );
      }
      if (idx < 0) {
        nextRows.push(withInHouseDefaultMatchFlags(entryRow));
        idx = nextRows.length - 1;
      }
    }

    if (idx < 0) return;

    const reason = detailReason(detail);
    if (!reason) return;

    if (isMethodologyOtherDetail(reason)) {
      nextRows[idx] = {
        ...nextRows[idx],
        "Methodology used": isOtherSpecified(nextRows[idx]?.["Methodology used"])
          ? nextRows[idx]["Methodology used"]
          : OTHER_SPECIFIED,
        matchedMethodologyUsed: false,
      };
      fieldErrors[idx] = {
        ...(fieldErrors[idx] || {}),
        [METHODOLOGY_OTHER_COL]: reason,
      };
      autoShowInput[`${idx}-Methodology used`] = true;
    } else if (isMethodologyUnmatchedDetail(reason)) {
      nextRows[idx] = {
        ...nextRows[idx],
        matchedMethodologyUsed: false,
      };
      fieldErrors[idx] = {
        ...(fieldErrors[idx] || {}),
        "Methodology used": reason,
      };
    }

    if (isProjectTypeOtherDetail(reason)) {
      nextRows[idx] = {
        ...nextRows[idx],
        "Project Type": isOtherSpecified(nextRows[idx]?.["Project Type"])
          ? nextRows[idx]["Project Type"]
          : OTHER_SPECIFIED,
        matchedProjectType: false,
      };
      fieldErrors[idx] = {
        ...(fieldErrors[idx] || {}),
        [PROJECT_TYPE_OTHER_COL]: reason,
      };
      autoShowInput[`${idx}-Project Type`] = true;
    } else if (isProjectTypeUnmatchedDetail(reason)) {
      nextRows[idx] = {
        ...nextRows[idx],
        matchedProjectType: false,
      };
      fieldErrors[idx] = {
        ...(fieldErrors[idx] || {}),
        "Project Type": reason,
      };
    }
  });

  return { rows: nextRows, fieldErrors, autoShowInput };
}

/** Unwrap nested in-house bulk upload API payloads. */
export function normalizeInHouseBulkUploadResponse(responseData = {}) {
  if (!responseData || typeof responseData !== "object") return {};

  let data = { ...responseData };

  if (
    data.data &&
    typeof data.data === "object" &&
    !Array.isArray(data.data)
  ) {
    data = { ...data, ...data.data };
  }

  if (
    Array.isArray(data.data) &&
    !data.excelData?.length &&
    !data.finalData?.length &&
    !data.statusList?.length
  ) {
    data.excelData = data.data;
    if (!data.statusList?.length) {
      data.statusList = data.data;
    }
  }

  if (
    Array.isArray(data.details) &&
    data.details.length > 0 &&
    !data.hasUnmatched
  ) {
    data.hasUnmatched = true;
  }

  const extraExcelKeys = [
    "uploadedData",
    "parsedData",
    "uploadedRows",
    "bulkData",
    "sheetData",
  ];
  for (const key of extraExcelKeys) {
    if (Array.isArray(data[key]) && !data.excelData?.length) {
      data.excelData = data[key];
      if (!data.statusList?.length) {
        data.statusList = data[key];
      }
      break;
    }
  }

  return data;
}

/** Build review rows from in-house bulk upload error/success payload. */
export function buildInHouseBulkUploadReviewRows(
  responseData = {},
  matchedKey,
  fallbackExcelRows = [],
) {
  const normalized = normalizeInHouseBulkUploadResponse(responseData);

  let excelRows = Array.isArray(normalized.excelData)
    ? normalized.excelData
    : Array.isArray(normalized.finalData)
      ? normalized.finalData
      : Array.isArray(normalized.rows)
        ? normalized.rows
        : Array.isArray(normalized.records)
          ? normalized.records
          : [];

  if (!excelRows.length && Array.isArray(fallbackExcelRows) && fallbackExcelRows.length) {
    excelRows = fallbackExcelRows;
  }

  excelRows = filterInHouseRowsWithMonth(excelRows);

  const detailRows = extractRowsFromDetails(normalized.details);
  if (!excelRows.length && detailRows.length) {
    excelRows = filterInHouseRowsWithMonth(detailRows);
  }

  const statusList = Array.isArray(normalized[matchedKey])
    ? normalized[matchedKey]
    : Array.isArray(normalized.statusList)
      ? normalized.statusList
      : [];

  let baseRows = [];
  if (excelRows.length > 0 && statusList.length > 0) {
    baseRows = excelRows.map((row) => {
      const statusIndex =
        Number.isFinite(Number(row?.__excelRowNumber)) &&
        row.__excelRowNumber > 1
          ? row.__excelRowNumber - 2
          : null;
      const status =
        statusIndex !== null && statusIndex >= 0
          ? statusList[statusIndex]
          : null;
      return {
        ...row,
        ...(status && typeof status === "object" ? status : {}),
      };
    });
  } else if (statusList.length > 0) {
    baseRows = filterInHouseRowsWithMonth(
      statusList.map((row) =>
        typeof row === "object" ? { ...row } : { value: row },
      ),
    );
  } else if (excelRows.length > 0) {
    baseRows = excelRows.map((row) =>
      withInHouseDefaultMatchFlags(normalizeInHouseBulkUploadRow(row)),
    );
  }

  const { rows, fieldErrors, autoShowInput } = applyInHouseBulkUploadDetails(
    baseRows,
    normalized.details,
  );

  return {
    rows,
    fieldErrors,
    autoShowInput,
    warningMessage: getInHouseBulkUploadErrorMessage(normalized),
    hasReviewData:
      rows.length > 0 ||
      Boolean(normalized.hasUnmatched) ||
      (Array.isArray(normalized.details) && normalized.details.length > 0),
  };
}

export function getInHouseBulkUploadErrorMessage(responseData = {}) {
  if (responseData?.error) return String(responseData.error);
  if (responseData?.message) return String(responseData.message);
  const fromDetails = (responseData?.details || [])
    .map((d) => detailReason(d))
    .filter(Boolean)
    .join(" ");
  return fromDetails || "Please review and resolve bulk upload data.";
}
